import { PoolRole, RemittanceStatus } from "@/services/graphql/reactQuery";
import {
  createFileRoute,
  notFound,
  useNavigate,
  useRouteContext,
  useRouter,
} from "@tanstack/react-router";

import { EmptyState } from "@/routes/-solverDemo/-internal/SolverComponents";
import { hasRemittanceAccess } from "@/services/auth/remittanceAccess";

import { PageContentContainer } from "@/shared/components/PageContentContainer";
import { AnalyticsConcentrations } from "@/shared/components/PoolDetails/AnalyticsConcentrations";
import { PoolLoanList } from "@/shared/components/PoolDetails/PoolLoanList";
import { dateFormatter } from "@/shared/utility/stringFormatters";
import { getJwtPayload } from "@ncino/pa-authentication-client-webclient";
import {
  Box,
  Card,
  Divider,
  DropdownOption,
  PaxDropdown,
  PaxErrorPage,
  PaxTab,
  PaxTabPanel,
  Stack,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTranslation,
} from "@ncino/pax-ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import z from "zod";
import {
  AnalyticsReport,
  LoanPoolItem,
  ManageAnalyticsReportKeys,
  ManageAnalyticsReports,
  ManageDetailTabKeys,
  ManageDetailTabs,
  PoolOrigination,
  PoolOriginationSchema,
} from "./-internal/dataTypes";
import { useManageRouteContext } from "./-internal/ManageContext";
import {
  ChargeOffPanel,
  DelinquencyPanel,
  OverviewPanel,
} from "./-internal/poolDetailPanels/analyticsPanels";
import {
  fetchPurchasedPools,
  fetchRemittancePools,
  fetchSoldPools,
  getDataDatesFromSellDate,
} from "./-internal/util";
import { RemittanceReportsList } from "./-internal/RemittanceReports/remittanceReportsList";
import { useIsPermissionGroupMemberQuery } from "@/services/auth/permissions";

export const Route = createFileRoute("/manage/$poolId")({
  component: RouteComponent,
  beforeLoad: ({ params }) => {
    const { poolId } = params;
    const [origin] = poolId.split("-");
    if (!poolId.match(/(.+)-(\d+)/g) || !PoolOriginationSchema.safeParse(origin).success) {
      throw notFound();
    }
  },
  validateSearch: z.object({
    tab: ManageDetailTabs.catch("analytics"),
    report: ManageAnalyticsReports.catch("overview"),
  }),
  loader: async ({ params, context }) => {
    const institutionId = context.institutionId;
    // const canCreateRemittance =
    const [origin, dbPoolId] = params.poolId.split("-");

    let poolName: string | undefined;
    let loanCount: number | undefined;
    let soldDate: string | undefined;
    let remittanceStatus: RemittanceStatus | undefined;
    let reportingPeriod: string | undefined;
    let canCreateRemittance: boolean;
    try {
      const token = await context.auth.tokenProvider();
      const principalId = getJwtPayload(token.token ?? "").sub;
      const allowed = await hasRemittanceAccess(
        principalId,
        institutionId,
        context.auth.tokenProvider,
        context.auth.authClient
      );
      canCreateRemittance = allowed;
    } catch (err) {
      if (import.meta.env.DEV) {
        console.debug("[remittanceAccess] Access check failed, defaulting to denied.", err);
      }
      canCreateRemittance = false;
    }

    if (canCreateRemittance) {
      const poolsData = await fetchRemittancePools(
        institutionId,
        origin === "Sold" ? PoolRole.Seller : PoolRole.Buyer,
        context
      );
      const pool = poolsData.remittanceHistories.find((p) => String(p.pool?.id) === dbPoolId);
      if (!pool) throw notFound();
      poolName = pool.pool?.poolName ?? undefined;
      loanCount = pool.pool?.loanCount;
      soldDate = pool.pool?.listing?.soldDate ?? undefined;
      remittanceStatus = pool.status;
      reportingPeriod = pool.reportingPeriod;
    } else {
      if (origin === "Sold") {
        const poolsData = await fetchSoldPools(institutionId, context);
        const soldPool = poolsData.pools
          ?.filter((pool) => pool.participants?.find((p) => p.buyerId === institutionId)?.isSeller)
          ?.find((p) => String(p.id) === dbPoolId);
        if (!soldPool) throw notFound();
        poolName = soldPool.poolName ?? undefined;
        loanCount = soldPool.loanCount;
        soldDate = soldPool.listing?.soldDate ?? undefined;
        remittanceStatus = RemittanceStatus.ReadyToReview;
        reportingPeriod = "-";
      } else {
        const poolsData = await fetchPurchasedPools(institutionId, context);
        const purchasedPool = poolsData.poolParticipants
          ?.filter((p) => p.isSeller === false)
          ?.find((p) => String(p.pool?.id) === dbPoolId);
        if (!purchasedPool) throw notFound();
        poolName = purchasedPool.pool?.poolName ?? undefined;
        loanCount = purchasedPool.pool?.loanCount;
        soldDate = purchasedPool.pool?.listing?.soldDate ?? undefined;
        remittanceStatus = RemittanceStatus.ReadyToReview;
        reportingPeriod = "-";
      }
    }

    return {
      isError: false,
      poolName,
      loanCount: loanCount ?? -1,
      soldDate,
      remittanceStatus,
      reportingPeriod,
    };
  },
});

function RouteComponent() {
  const { t } = useTranslation("manage");
  const navigate = useNavigate({ from: "/manage/$poolId" });
  const { openLoanPools, addPoolTab } = useManageRouteContext();
  const router = useRouter();
  const { poolId } = Route.useParams();
  const [origin, dbPoolId] = poolId.split("-");
  const { tab, report } = Route.useSearch();
  const { portfolioId, institutionId } = useRouteContext({
    from: "/manage",
  });

  const { data: canViewRemittance = false } = useIsPermissionGroupMemberQuery("approveRemittance");

  const [selectedTab, setSelectedTab] = useState<ManageDetailTabKeys>(tab);
  const [selectedReport, setSelectedReport] = useState<ManageAnalyticsReportKeys>(report);

  // Fall back off the remittance tab if access is (or becomes) denied — inline
  // pattern avoids setState-in-effect for the local state correction.
  if (selectedTab === "remittanceReports" && !canViewRemittance) {
    setSelectedTab("analytics");
  }

  const {
    poolName,
    loanCount: loaderLoanCount,
    isError,
    soldDate,
    remittanceStatus,
    reportingPeriod,
  } = Route.useLoaderData();
  const sellDate = soldDate ?? "";
  const [dataDates, setDataDates] = useState(
    getDataDatesFromSellDate(sellDate).sort((a, b) => b.localeCompare(a))
  );
  const [dataDate, setDataDate] = useState([dataDates[0]]);

  const dataDateFormatter = useCallback(() => dateFormatter("en-US", true), []);
  const dataDateOpts = useMemo<DropdownOption[]>(() => {
    return dataDates.map((date) => ({
      label: dataDateFormatter().format(new Date(`${date}T00:00:00`)),
      value: date,
    }));
  }, [dataDates]);

  const loanPool = useMemo((): LoanPoolItem => {
    if (poolId in openLoanPools) return openLoanPools[poolId];
    return {
      name: poolName ?? "—",
      origin: origin as PoolOrigination,
      id: Number(dbPoolId),
      tabId: poolId,
      count: loaderLoanCount ?? -1,
      status: remittanceStatus,
      reportingPeriod: reportingPeriod,
    };
  }, [poolId, poolName, loaderLoanCount, openLoanPools]);

  useEffect(() => {
    if (!(poolId in openLoanPools)) {
      addPoolTab(loanPool);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolId]);

  useEffect(() => {
    if (tab === "remittanceReports" && !canViewRemittance) {
      navigate({ search: (prev) => ({ ...prev, tab: "analytics" }) });
    }
  }, [tab, canViewRemittance, navigate]);

  const { name: loanPoolName, count: loanCount } = loanPool;
  const loanCountLabel = loanCount === -1 ? "-" : `${loanCount}`;

  function retryQueries() {
    router.invalidate();
  }

  if (isError) {
    return (
      <PaxErrorPage
        header={t("poolSection.errorTitle")}
        subtext={t("poolSection.errorSubtext")}
        actionLabel={t("managePoolsPage.tryAgain")}
        onAction={retryQueries}
      />
    );
  }

  if (!dataDateOpts || dataDateOpts.length === 0) {
    return (
      <EmptyState
        message="No loan data available for this pool yet."
        submessage={`If this pool was recently ${loanPool.origin === "Sold" ? "sold" : "purchased"}, data will be available at the next month-end.`}
      />
    );
  }

  return (
    <PageContentContainer>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6">{loanPoolName}</Typography>
        </Box>
        <Box minWidth={"200px"}>
          <PaxDropdown
            label={t("managePoolDetail.dataDateLabel")}
            options={dataDateOpts}
            selectedValues={dataDate}
            onChange={(dd) => setDataDate(dd)}
            buttonSx={{
              fontSize: "1rem",
            }}
          />
        </Box>
      </Stack>

      <Tabs
        value={selectedTab}
        onChange={(_, t) => {
          setSelectedTab(t);
          navigate({
            search: (prev) => ({ ...prev, tab: t }),
          });
        }}
      >
        <PaxTab
          label={t("managePoolDetail.tabs.analytics")}
          aria-label={t("managePoolDetail.tabs.analytics")}
          value={"analytics"}
        />
        <PaxTab
          label={t("managePoolDetail.tabs.loans") + ` (${loanCountLabel})`}
          aria-label={t("managePoolDetail.tabs.loans") + ` (${loanCountLabel})`}
          value={"loanList"}
        />
        {canViewRemittance && (
          <PaxTab
            label={t("managePoolDetail.tabs.remittanceReports")}
            aria-label={t("managePoolDetail.tabs.remittanceReports")}
            value={"remittanceReports"}
          />
        )}
      </Tabs>
      <Divider sx={{ mb: 4 }} />

      <Box>
        {selectedTab === "analytics" ? (
          <Card sx={{ padding: "20px" }} elevation={0} variant="outlined">
            {/* Analytics */}
            <PaxTabPanel value={"analytics"} index={selectedTab}>
              <Box display={"flex"} justifyContent={"right"} mb={4}>
                <ToggleButtonGroup
                  exclusive
                  value={selectedReport}
                  onChange={(_, mode) => {
                    if (mode == null) {
                      return;
                    }

                    setSelectedReport(mode);
                    navigate({
                      search: (prev) => ({ ...prev, report: mode }),
                    });
                  }}
                >
                  <ToggleButton value={AnalyticsReport.OVERVIEW}>
                    {t("managePoolDetail.analytics.overview")}
                  </ToggleButton>
                  <ToggleButton value={AnalyticsReport.CONCENTRATION}>
                    {t("managePoolDetail.analytics.concentrations")}
                  </ToggleButton>
                  <ToggleButton value={AnalyticsReport.CHARGE_OFF}>
                    {t("managePoolDetail.analytics.chargeOff")}
                  </ToggleButton>
                  <ToggleButton value={AnalyticsReport.DELINQUENCY}>
                    {t("managePoolDetail.analytics.delinquency")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <PaxTabPanel value={AnalyticsReport.OVERVIEW} index={selectedReport}>
                <OverviewPanel portfolioId={portfolioId} poolId={dbPoolId} dataDate={dataDate[0]} />
              </PaxTabPanel>

              <PaxTabPanel value={AnalyticsReport.CONCENTRATION} index={selectedReport}>
                <AnalyticsConcentrations
                  portfolioId={portfolioId}
                  dataDate={dataDate[0]}
                  poolId={dbPoolId}
                />
              </PaxTabPanel>

              <PaxTabPanel value={AnalyticsReport.CHARGE_OFF} index={selectedReport}>
                <ChargeOffPanel
                  portfolioId={portfolioId}
                  poolId={dbPoolId}
                  dataDate={dataDate[0]}
                />
              </PaxTabPanel>

              <PaxTabPanel value={AnalyticsReport.DELINQUENCY} index={selectedReport}>
                <DelinquencyPanel
                  portfolioId={portfolioId}
                  poolId={dbPoolId}
                  dataDate={dataDate[0]}
                />
              </PaxTabPanel>
            </PaxTabPanel>
          </Card>
        ) : selectedTab === "loanList" ? (
          //  Loan List - mirroring buy loan list, no wrapping card
          <PaxTabPanel value={"loanList"} index={selectedTab}>
            <PoolLoanList
              portfolioId={portfolioId}
              poolId={dbPoolId}
              dataDate={dataDate[0]}
              totalLoans={loanCount}
            />
          </PaxTabPanel>
        ) : selectedTab === "remittanceReports" && canViewRemittance ? (
          <PaxTabPanel value={"remittanceReports"} index={selectedTab}>
            <RemittanceReportsList
              portfolioId={portfolioId}
              poolId={dbPoolId}
              institutionId={institutionId}
            />
          </PaxTabPanel>
        ) : null}
      </Box>
    </PageContentContainer>
  );
}
